// PROD-3182 — the Assessment & Plan as the ambient panel reads it, and the duplicate-order guard.
//
// Pure. The page owns the canvas state (diagnoses, orders, the payload side-map); these two
// functions read it. One reads it FOR the panel (what is already on the plan, so a suggestion can
// say "Already added"). The other reads it BEFORE an add (is this code already ordered), so the
// same drug cannot go on the plan twice, as `handleAddDx` already refuses a second diagnosis.

import type { PlanItem } from '../ambient/plan-match';
import { orderCodeOf } from '../order-sets/order-set-apply';
import { findDuplicateOrders } from '../order-sets/order-set-duplicates';
import type { CodedOrderPayload, OrderPayloadMap } from './order-payload';
import type { DxCard, PlanOrder } from './to-patient-assessment';

/** The names an order row is known by: its label, and the drug / test name on its payload. */
function orderNamesOf(order: PlanOrder, payload: CodedOrderPayload | undefined): string[] {
  const names = [order.label];
  if (payload?.kind === 'medication') names.push(payload.medicationCode.displayName);
  if (payload?.kind === 'lab' || payload?.kind === 'imaging' || payload?.kind === 'procedure') {
    names.push(payload.code.displayName);
  }
  if (payload?.kind === 'vaccination' && payload.entry.vaccine?.display) names.push(payload.entry.vaccine.display);
  return [...new Set(names.filter((n): n is string => !!n && !!n.trim()))];
}

/** Every diagnosis and every order on the plan, for the ambient panel (`AmbientScribePanelProps.planItems`). */
export function toPlanItems(
  diagnoses: readonly DxCard[],
  orders: readonly PlanOrder[],
  payloads: OrderPayloadMap,
): PlanItem[] {
  const items: PlanItem[] = diagnoses.map((dx) => ({
    kind: 'diagnosis',
    ...(dx.code ? { code: dx.code } : {}),
    names: [dx.display],
  }));
  for (const order of orders) {
    const payload = payloads.get(order.id);
    const code = orderCodeOf(payload)?.code?.trim();
    items.push({ kind: order.type, ...(code ? { code } : {}), names: orderNamesOf(order, payload) });
  }
  return items;
}

/**
 * The labels of the orders already on the plan that `payload` would duplicate, or [] when none.
 *
 * THE SAME RULES THE ORDER SETS USE (`order-set-duplicates.ts`, PROD-2721 AC2): same lab code or a
 * panel whose every member is ordered; same imaging code unless laterality or views differ; same
 * drug code; same vaccine code and dose number; same procedure code. One rule set, so "already
 * ordered" means the same thing from a dialog and from an order set. A referral is never a
 * duplicate (it has no code).
 */
export function findDuplicateOrder(
  orders: readonly PlanOrder[],
  payloads: OrderPayloadMap,
  payload: CodedOrderPayload,
): string[] {
  return findDuplicateOrders(
    payload,
    orders.map((o) => ({ label: o.label, payload: payloads.get(o.id) })),
  );
}
